<template>
  <div>
    <el-row
      :gutter="20"
      align="middle"
    >
      <el-col
        class="stepTitle"
        :span="4"
      >
        <span class="label">
          {{ $t('textFileUrl.title') }}
        </span>
      </el-col>
      <el-col
        :span="17"
        :offset="2"
      >
        <el-input
          class="fileUrl"
          :placeholder="$t('textFileUrl.inputPlaceholder')"
          readonly
          v-model="textFileUrl"
        >
          <span slot="suffix">
            <el-tooltip
              effect="dark"
              :content="$t('textFileUrl.copy')"
              placement="top"
              popper-class="copy-tooltip"
            >
              <el-button
                type="text"
                @click="handleCopy"
              >
                <i
                  class="el-icon-copy-document"
                  tabindex="-1"
                ></i>
              </el-button>
            </el-tooltip>
          </span>
        </el-input>
        <div v-if="status === 'default'">
          <progress-bar key="default" />
          <div class="progressText">{{ $t('textFileUrl.verifyingOwnership') }} <span style="font-weight: bold;">{{targetUrl}}</span> ...</div>
        </div>
        <div
          class="progress"
          v-if="status === 'progress'"
        >
          <progress-bar key="progress" />
          <div class="progressText">{{ $t('textFileUrl.accessingTo') }} {{targetUrl}} ...</div>
        </div>
        <div
          class="failure"
          v-else-if="status=== 'failure'"
        >
          <i class="el-icon-error errorIcon"></i>
          <i18n
            path="textFileUrl.verificationFailed"
            tag="span"
          >
            <span v-if="errorMsg">{{errorMsg}} </span>
          </i18n>
        </div>
        <div
          class="success"
          v-else-if="status === 'success'"
        >
          <i class="el-icon-success successIcon"></i>
          <span v-if="!haveVerified">{{ $t('textFileUrl.verificationPassed') }}</span>
          <span v-else>{{ $t('textFileUrl.ownershipVerified') }} <span style="font-weight: bold;">{{domain}}</span> {{ $t('textFileUrl.hasBeenVerified') }}</span>
        </div>
        <div
          class="init"
          v-if="status === 'init' || status === 'failure'"
        >
          <el-tag
            type="info"
            class="infoTag"
          >
            <ul>
              <li>{{$t('textFileUrl.instructionPart2')}}</li>
              <i18n
                path="textFileUrl.instructionPart3"
                tag="li"
              >
                <span style="font-weight: bold;">{{textFileUrl}}</span>
              </i18n>
              <li>
                <div style="display: flex; align-items: center;">
                  <i18n
                    path="textFileUrl.instructionPart4"
                    tag="span"
                  >
                    {{textFileName}}
                  </i18n>
                  <clip-text
                    tabWidth="495px"
                    type="warning"
                  >
                    {{randomContent}}
                  </clip-text>
                </div>
              </li>
              <i18n
                path="textFileUrl.instructionPart5"
                tag="li"
              >
                <el-link
                  href="/document"
                  target="_blank"
                  type="warning"
                >
                  {{documentUrl}}
                </el-link>
              </i18n>
            </ul>
          </el-tag>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import ClipText from '@/components/clip-text.vue';
import ProgressBar from '@/components/progress-bar.vue';
import { copyText } from '@/utils/copy-text';
import { joinPaths } from '@/utils/join-path';
import { getRandomContent, verifyDomainOwnership } from '@/api/base';

export default {
  name: 'TextFileUrl',
  components: {
    ClipText,
    ProgressBar,
  },
  props: {
    payload: {
      type: Object,
    },
  },
  data() {
    const { url } = this.payload;
    const protocol = new URL(url).protocol;
    const domain = new URL(url).host;
    const targetUrl = `${protocol}//${domain}`;
    const textFileName = process.env.VUE_APP_TEXT_FILE_NAME;

    return {
      domain,
      url,
      targetUrl,
      textFileUrl: joinPaths(targetUrl, `/.well-known/${textFileName}`),
      status: 'default',
      randomContent: '',
      haveVerified: false,
      textFileName,
      errorMsg: '',
      documentUrl: `${location.origin}/document`,
    };
  },
  created() {
    this.checkHasVerified();
  },
  watch: {
    status: {
      immediate: true,
      handler(val) {
        if (val === 'default') {
          this.$emit('next-disabled', true);
        } else {
          this.$emit('next-disabled', false);
        }

        if (val === 'progress') {
          this.$emit('next-loading', true);
        } else {
          this.$emit('next-loading', false);
        }
      },
    },
  },
  methods: {
    handleCopy() {
      copyText(this.textFileUrl);
    },
    checkHasVerified() {
      const params = {
        fetchUrl: this.textFileUrl,
      };

      getRandomContent().then((res) => {
        this.randomContent = res?.content || '';
      });

      return verifyDomainOwnership(params, true)
        .then(() => {
          this.status = 'success';
          this.haveVerified = true;
        })
        .catch(() => {
          this.status = 'init';
        });
    },
    next() {
      if (this.status === 'success') {
        const { stageName } = this.payload;

        return {
          stage: stageName || 'TextFileUrl',
        };
      }

      if (this.status === 'init' && this.randomContent) {
        this.status = 'progress';

        const params = {
          fetchUrl: this.textFileUrl,
          verifyContent: this.randomContent,
        };

        verifyDomainOwnership(params)
          .then(() => {
            this.status = 'success';
          })
          .catch(() => {
            // const { errMsg, detail } = error;

            this.status = 'failure';
            // this.errorMsg = detail || errMsg;
          });
      }

      if (this.status === 'failure') {
        this.status = 'init';
        this.next();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.label {
  font-weight: bold;
  line-height: 40px;
  white-space: nowrap;
}
.flex-row {
  display: flex;
  align-items: center;
}
.placeholder {
  display: block;
  height: 40px;
}
.infoTag {
  margin-top: 20px;
  width: 100%;
  overflow: hidden;
  word-wrap: break-word;
  white-space: break-spaces;
  height: fit-content;
  line-height: 2;
  font-size: 16px;
  background: #e1e8ed;
}
.margin-top {
  margin-top: 20px;
}

.progressText {
  margin-top: 10px;
  text-align: center;
  color: #909399;
}

.errorIcon {
  display: inline-block;
  margin-right: 10px;
  color: #f56c6c;
  font-size: 20px;
}

.successIcon {
  display: inline-block;
  margin-right: 10px;
  color: #67c23a;
  font-size: 20px;
}

.fileUrl {
  margin-bottom: 40px;
}
</style>

<style lang="scss">
.copy-tooltip {
  &.el-tooltip__popper {
    width: fit-content !important;
  }
}
</style>